import React, { useState, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Sticky from 'react-stickynode';
import cx from 'classnames';
import { connect, useSelector } from 'react-redux';
import { setMultiSampleEnabled as setMultiSampleEnabledAction } from 'cards/actions/samples';
import MultiSample from 'cards/components/common/Samples/MultiSample/MultiSample';
import SortBy from 'cards/components/CardListing/CardListingRedesign/SortBy';
import UploadPlaceholder from 'cards/components/CardListing/PhotoPreview/Upload/UploadPlaceholder';
import { getCardSettings } from 'cards/util/cardSettingsUtils';
import { getColorOptionList } from 'cards/constants/seoColors';
import { CARD_TYPE_MAP } from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { getDesktopNavHeight } from 'components/common/header/helpers';
import { CARD_SHAPE_FILTERS } from 'cards/constants/CardShapeOptions';
import { getCardCatalogBusy } from 'cards/selectors/cardCatalogSelector';
import FilterTags from './FilterTags/FilterTags';
import { CardListingMobileHeader } from '../CardListingMobileHeader';

import {
  MotifMenu,
  ColorMenu,
  MagnetMenu,
  CustomPhotoMenu,
  CustomFoilMenu,
  ShapeMenu,
  LetterpressMenu,
  ReceptionMenu,
  OrientationMenu,
  FeaturedArtistMenu,
  MobileOrientationMenu,
  MobileCustomPhotoMenu,
  CtdOrientationMenu,
} from './Menus/Menus';
import styles from './styles.module.less';
import PlpRedesignFilterMenu from './PlpRedesignFilterMenu';
import { isPhotoPreviewSupported } from '../../PhotoPreview/constants';

const Upload = dynamic(() => import('cards/components/CardListing/PhotoPreview/Upload'), {
  loading: () => <UploadPlaceholder />,
  ssr: false,
});

// Filter Locations
const MORE_MENU = 'MORE_MENU';
const FILTER_BUTTON_LIST = 'FILTER_BUTTON_LIST';

const CardListingFilter = ({
  onUpdateSearchFilters,
  searchFilters,
  handleFilterUpdate,
  allMotifs,
  userContext,
  scrollActive,
  setScrollActive,
  activeCardEvent,
  multiSampleState,
  setMultiSampleEnabled,
  allowMultiSample,
  clearFilters,
  scrollToTop,
  isMobile,
  isDesktop,
  searchTotal = 0,
}) => {
  const isMatchingSuites = activeCardEvent === CARD_TYPE_MAP.matchingSuites;
  const busy = useSelector(getCardCatalogBusy);
  const [menuDirection, setMenuDirection] = useState('down');
  const { digital_suite: isDigital, lead_card_type: cardType } = searchFilters;
  const cardSettings = isMatchingSuites
    ? getCardSettings('MATCHING_SUITES', isDigital)
    : getCardSettings(cardType, isDigital);

  function onDirectionChange(newDirection) {
    setMenuDirection(newDirection.detail.direction);
  }

  useEffect(() => {
    window.addEventListener('NAV_DIRECTION', onDirectionChange);
    return () => {
      window.removeEventListener('NAV_DIRECTION', onDirectionChange);
    };
  }, []);

  useEffect(() => {
    if (allowMultiSample) {
      setMultiSampleEnabled(cardType);
    }
  }, [allowMultiSample, cardType, setMultiSampleEnabled]);

  // height of main nav
  const TOP_OFFSET = useMemo(() => {
    if (!isDesktop) return 58;

    if (userContext.is_guest) {
      return 133;
    }

    return menuDirection === 'down' ? 104 : getDesktopNavHeight(false, userContext.is_guest);
  }, [isDesktop, menuDirection, userContext.is_guest]);

  const filterMenuList = useMemo(() => {
    const isCtd = activeCardEvent === CARD_TYPE_MAP.ctd;
    const isHoliday = activeCardEvent === CARD_TYPE_MAP.holiday;
    const {
      hasMotifs,
      hasLetterpress,
      hasMultipleOrientations,
      hasThirdPartyDesigns,
      hasFoil,
      hasShapes,
      hasNoCustomPhoto,
      hasCustomPhoto,
      isVowRenewal,
    } = cardSettings;

    return [
      {
        clearValue: {
          motifs: [],
        },
        content: MotifMenu,
        mobileContent: null,
        filterList: ['motifs'],
        filterStateKey: 'motifs',
        label: isHoliday ? 'Occasion' : 'Style',
        newBadge: false,
        optionList: allMotifs,
        numSelected: () => {
          return searchFilters.motifs.length;
        },
        isActive: searchFilters.motifs.length > 0,
        visibility: hasMotifs,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: FILTER_BUTTON_LIST,
        },
      },
      {
        clearValue: {
          multiColor: [],
        },
        content: ColorMenu,
        mobileContent: null,
        filterList: ['multiColor'],
        filterStateKey: 'multiColor',
        label: 'Color',
        newBadge: false,
        optionList: getColorOptionList(activeCardEvent),
        numSelected: () => {
          const multiColorLength = searchFilters?.multiColor?.length || 0;
          const seoColorLength = searchFilters?.seo_colors?.length || 0;
          return multiColorLength + seoColorLength;
        },
        isActive: !!(
          searchFilters?.multiColor?.length > 0 || searchFilters?.seo_colors?.length > 0
        ),
        visibility: true,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: FILTER_BUTTON_LIST,
        },
      },
      {
        clearValue: {
          silhouettes: [],
        },
        content: ShapeMenu,
        mobileContent: null,
        filterList: ['silhouettes'],
        filterStateKey: 'silhouettes',
        label: 'Shape',
        newBadge: false,
        optionList: CARD_SHAPE_FILTERS,
        numSelected: () => searchFilters.silhouettes.length,
        isActive: searchFilters.silhouettes.length > 0,
        visibility: hasShapes,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: FILTER_BUTTON_LIST,
        },
      },
      {
        clearValue: {
          has_magnet: false,
        },
        content: MagnetMenu,
        mobileContent: MagnetMenu,
        filterList: ['has_magnet'],
        filterStateKey: 'has_magnet',
        label: 'Magnet',
        newBadge: false,
        numSelected: () => null,
        isActive: searchFilters.has_magnet,
        visibility: cardType === CARD_TYPE_MAP.std && !isDigital,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: {
          has_custom_photo: false,
          has_no_custom_photo: false,
        },
        content: CustomPhotoMenu,
        mobileContent: MobileCustomPhotoMenu,
        filterList: ['has_custom_photo', 'has_no_custom_photo'],
        individualVisibilityCheck: {
          has_custom_photo: hasCustomPhoto,
          has_no_custom_photo: hasNoCustomPhoto,
        },
        label: 'Photo',
        newBadge: false,
        numSelected: () => {
          let total = 0;
          if (searchFilters.has_no_custom_photo) total += 1;
          if (searchFilters.has_custom_photo) total += 1;
          return total || null;
        },
        isActive: searchFilters.has_no_custom_photo || searchFilters.has_custom_photo,
        visibility: hasNoCustomPhoto || hasCustomPhoto,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: {
          has_foil: false,
        },
        content: CustomFoilMenu,
        mobileContent: CustomFoilMenu,
        filterList: ['has_foil'],
        filterStateKey: 'has_foil',
        label: 'Foil',
        newBadge: false,
        numSelected: () => null,
        isActive: searchFilters.has_foil,
        visibility: hasFoil,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: { is_letterpress: false },
        content: LetterpressMenu,
        mobileContent: LetterpressMenu,
        filterList: ['is_letterpress'],
        filterStateKey: 'is_letterpress',
        label: 'Letterpress',
        newBadge: false,
        numSelected: () => null,
        isActive: searchFilters.is_letterpress,
        visibility: hasLetterpress,
        display: {
          desktop: FILTER_BUTTON_LIST,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: {
          is_vow_renewal: false,
        },
        content: ReceptionMenu,
        mobileContent: ReceptionMenu,
        filterList: ['is_vow_renewal'],
        filterStateKey: 'is_vow_renewal',
        label: 'Reception Only',
        newBadge: false,
        numSelected: () => null,
        isActive: searchFilters.is_vow_renewal,
        visibility: isVowRenewal,
        display: {
          desktop: MORE_MENU,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: {
          is_landscape: false,
          is_portrait: false,
        },
        content: isCtd ? CtdOrientationMenu : OrientationMenu,
        mobileContent: MobileOrientationMenu,
        filterList: ['is_landscape', 'is_portrait'],
        label: 'Orientation',
        newBadge: false,
        numSelected: () => {
          let total = 0;
          if (searchFilters.is_landscape) total += 1;
          if (searchFilters.is_portrait) total += 1;
          return total || null;
        },
        isActive: searchFilters.is_landscape || searchFilters.is_portrait,
        visibility: hasMultipleOrientations,
        display: {
          desktop: isCtd ? FILTER_BUTTON_LIST : MORE_MENU,
          mobile: MORE_MENU,
        },
      },
      {
        clearValue: {
          featured_artist: false,
        },
        content: FeaturedArtistMenu,
        mobileContent: FeaturedArtistMenu,
        filterList: ['featured_artist'],
        filterStateKey: 'featured_artist',
        label: 'Artist',
        newBadge: false,
        numSelected: () => null,
        isActive: searchFilters.featured_artist,
        visibility: hasThirdPartyDesigns,
        display: {
          desktop: MORE_MENU,
          mobile: MORE_MENU,
        },
      },
    ].filter(item => !!item); // Filter out falsy values
  }, [searchFilters, cardSettings, allMotifs, cardType, activeCardEvent, isDigital]);

  const isSampleModeActive = allowMultiSample && multiSampleState.enabled;
  const showMultiSample = allowMultiSample && !isMatchingSuites;
  const showSortBy =
    !isMatchingSuites &&
    isDesktop &&
    ((scrollActive && !multiSampleState.enabled) || !scrollActive);
  const isPlpPhotoPreview = isPhotoPreviewSupported(cardType);
  const showPhotoPreviewButton = !multiSampleState.enabled && isPlpPhotoPreview;

  if (isMobile) {
    return (
      <CardListingMobileHeader
        filterMenuList={filterMenuList.filter(filter => filter.visibility)}
        clearFilters={clearFilters}
        updateFilter={handleFilterUpdate}
        searchTotal={searchTotal}
        searchFilters={searchFilters}
      />
    );
  }

  return (
    <>
      <div className={cx(styles.listingFilterContainer, 'card-listing-filter-container')}>
        {multiSampleState.enabled && showMultiSample && (
          <div className={cx(styles.multiSampleEnabledRow)}>
            <MultiSample.ToggleLabel />
            <MultiSample.Toggle cardType={cardType} scrollToTop={scrollToTop} />
            <MultiSample.CustomizeButton />
          </div>
        )}
        <Sticky
          innerZ="20"
          onStateChange={() => setScrollActive(!scrollActive)}
          activeClass={styles.topFiltersActive}
          top={TOP_OFFSET}
        >
          <div className={cx(styles.listingFilter, 'card-listing-filter')}>
            <div
              className={cx(styles.filterBar, {
                [styles.filterBarWithSampleSelection]: multiSampleState.enabled,
              })}
            >
              {(!isSampleModeActive || (!isMatchingSuites && !scrollActive)) && (
                <div
                  className={cx(styles.filterContainer, {
                    [styles.filterContainerWithToggle]: allowMultiSample,
                  })}
                >
                  <PlpRedesignFilterMenu
                    filterMenuList={filterMenuList}
                    searchFilters={searchFilters}
                    onUpdateSearchFilters={onUpdateSearchFilters}
                  />
                </div>
              )}
              <div className={styles.rightPushedContainer}>
                {showMultiSample &&
                  (!multiSampleState.enabled || (multiSampleState.enabled && scrollActive)) && (
                    <div className={styles.multiSampleBox}>
                      <MultiSample.ToggleLabel />
                      <MultiSample.Toggle cardType={cardType} scrollToTop={scrollToTop} />
                      <MultiSample.CustomizeButton scrollActive={scrollActive} />
                    </div>
                  )}
                {showSortBy && (
                  <SortBy
                    sortBy={searchFilters.sorts}
                    handleFilterUpdate={handleFilterUpdate}
                    isDigital={isDigital}
                  />
                )}
              </div>
            </div>
            <FilterTags
              searchFilters={searchFilters}
              onClearAll={clearFilters}
              updateFilter={onUpdateSearchFilters}
              allMotifs={allMotifs}
              className={styles.plpFilterTagsContainer}
            />
          </div>
          <div className={styles.flex}>
            {!busy && searchTotal > 0 && (
              <div className={styles.totalResults} data-testid="plp-search-total">
                {searchTotal === 1 ? '1 result' : `${searchTotal} results`}
              </div>
            )}
            {showPhotoPreviewButton && <Upload updateFilter={handleFilterUpdate} />}
          </div>
        </Sticky>
      </div>
    </>
  );
};

CardListingFilter.propTypes = {
  onUpdateSearchFilters: PropTypes.func.isRequired,
  searchFilters: PropTypes.shape({
    lead_card_type: PropTypes.string,
    multiColor: PropTypes.array,
    motifs: PropTypes.array,
    silhouettes: PropTypes.array,
    has_foil: PropTypes.bool,
    has_custom_photo: PropTypes.bool,
    has_no_custom_photo: PropTypes.bool,
    has_digital: PropTypes.bool,
    has_magnet: PropTypes.bool,
    is_letterpress: PropTypes.bool,
    is_portrait: PropTypes.bool,
    is_landscape: PropTypes.bool,
    is_vow_renewal: PropTypes.bool,
    featured_artist: PropTypes.bool,
  }),
  allMotifs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      key: PropTypes.string,
    })
  ),
  userContext: PropTypes.shape({
    has_invitation_account: PropTypes.bool,
  }),
  scrollActive: PropTypes.bool,
  setScrollActive: PropTypes.func,
  activeCardEvent: PropTypes.string,
  scrollToTop: PropTypes.func,
  searchTotal: PropTypes.number,
};

const mapDispatchToProps = dispatch => ({
  setMultiSampleEnabled: cardType => dispatch(setMultiSampleEnabledAction(cardType)),
});

export default connect(null, mapDispatchToProps)(CardListingFilter);
